/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { getPlans } from "../core/_requests";
import { Plan } from "../core/_models";

// Plan context type definition
interface PlanContextType {
  plans: Plan[];
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
}

// PlanContext creation with default value of undefined
const PlanContext = createContext<PlanContextType | undefined>(undefined);

// Custom hook to use the PlanContext
export const usePlan = (): PlanContextType => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error("useChef must be used within a PlanProvider");
  }
  return context;
};

// ChefProvider props type definition
interface ChefProviderProps {
  children: ReactNode;
}

// PlanProvider component
const PlanProvider: React.FC<ChefProviderProps> = ({ children }) => {
  const userId = localStorage.getItem("userId");

  // Plans state initialization
  const [plans, setPlans] = useState<Plan[]>([]);

  useEffect(() => {
    const fetchPlans = async () => {
      if (userId) {
        try {
          const res = await getPlans(userId); // Fetch plans using the getPlans function
          setPlans(res?.data || []); // Set plans or an empty array if no data
        } catch (error) {
          console.error("Error fetching plans:", error); // Handle errors gracefully
        }
      }
    };
  
  }, [userId]); // Effect runs when userId changes

  return (
    <PlanContext.Provider value={{ plans, setPlans }}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanProvider;
